import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { useContract } from '../../hooks/useContract';
import { useDividend } from '../../hooks/useDividend';
import { formatTokenAmount, formatTimeRemaining } from '../../utils/formatters';
import { CONTRACT_ADDRESSES } from '../../contracts/addresses';
import './DividendPanel.css';

const DividendPanel = ({ walletData }) => {
  const { dividendContract } = useContract(walletData);
  const { 
    dividendData, 
    isLoading, 
    claimDividends, 
    isClaiming,
    error,
    refreshData 
  } = useDividend(dividendContract, walletData.account);

  const [cycleInfo, setCycleInfo] = useState({
    cycleId: 0,
    cycleStart: 0,
    timeUntilNext: 0,
    eligiblePlayerCount: 0,
    isPlayerEligible: false,
    lastEligibleCycle: 0
  });

  const [liquidityInfo, setLiquidityInfo] = useState({
    totalBalance: '0',
    pendingDividends: '0',
    availableLiquidity: '0'
  });

  useEffect(() => {
    if (dividendContract && walletData.account) {
      loadCycleInfo();
      loadLiquidityInfo();
      const interval = setInterval(() => {
        loadCycleInfo();
        loadLiquidityInfo();
      }, 10000); // Update every 10 seconds for real-time feel
      return () => clearInterval(interval);
    }
  }, [dividendContract, walletData.account]);

  const loadCycleInfo = async () => {
    try {
      if (!dividendContract) return;

      // Check if contract is deployed
      const code = await dividendContract.provider.getCode(dividendContract.address);
      if (code === '0x') {
        console.warn('Dividend contract not deployed');
        return;
      }

      const [cycleData, playerEligibility] = await Promise.all([
        dividendContract.getCurrentCycleInfo().catch(() => ({
          cycleId: ethers.BigNumber.from(0),
          cycleStart: ethers.BigNumber.from(0),
          timeUntilNext: ethers.BigNumber.from(0),
          eligiblePlayerCount: ethers.BigNumber.from(0)
        })),
        dividendContract.getPlayerEligibility(walletData.account).catch(() => ({
          isEligible: false,
          lastEligibleCycle: ethers.BigNumber.from(0)
        }))
      ]);

      setCycleInfo({
        cycleId: parseInt(cycleData.cycleId?.toString() || '0'),
        cycleStart: parseInt(cycleData.cycleStart?.toString() || '0'),
        timeUntilNext: parseInt(cycleData.timeUntilNext?.toString() || '0'),
        eligiblePlayerCount: parseInt(cycleData.eligiblePlayerCount?.toString() || '0'),
        isPlayerEligible: playerEligibility.isEligible || false,
        lastEligibleCycle: parseInt(playerEligibility.lastEligibleCycle?.toString() || '0')
      });
    } catch (error) {
      console.error('Error loading cycle info:', error);
      setCycleInfo({
        cycleId: 0,
        cycleStart: 0,
        timeUntilNext: 0,
        eligiblePlayerCount: 0,
        isPlayerEligible: false,
        lastEligibleCycle: 0
      });
    }
  };

  const loadLiquidityInfo = async () => {
    try {
      if (!dividendContract) return;

      const liquidityData = await dividendContract.getLiquidityStatus().catch(() => ({
        totalBalance: ethers.BigNumber.from(0),
        pendingDividends: ethers.BigNumber.from(0),
        availableLiquidity: ethers.BigNumber.from(0)
      }));

      setLiquidityInfo({
        totalBalance: ethers.utils.formatEther(liquidityData.totalBalance || 0),
        pendingDividends: ethers.utils.formatEther(liquidityData.pendingDividends || 0),
        availableLiquidity: ethers.utils.formatEther(liquidityData.availableLiquidity || 0)
      });
    } catch (error) {
      console.error('Error loading liquidity info:', error);
      setLiquidityInfo({
        totalBalance: '0',
        pendingDividends: '0',
        availableLiquidity: '0'
      });
    }
  };

  const handleClaim = async () => {
    try {
      await claimDividends();
      await refreshData();
      await loadCycleInfo();
      await loadLiquidityInfo();
    } catch (error) {
      console.error('Error claiming dividends:', error);
    }
  };

  const formatCycleTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  const getEligibilityStatus = () => {
    if (cycleInfo.isPlayerEligible) {
      return { text: 'Active Warrior ⚔️', class: 'eligible' };
    } else if (cycleInfo.lastEligibleCycle > 0) {
      return { text: 'Veteran Warrior 🎖️', class: 'veteran' };
    } else {
      return { text: 'Recruit 🛡️', class: 'recruit' };
    }
  };

  if (!walletData.account) {
    return (
      <div className="dividend-panel">
        <div className="dividend-header">
          <h2>🏰 Warriors' Treasury</h2>
          <p>Join the battle to earn legendary rewards</p>
        </div>
        <div className="connect-prompt">
          <div className="connect-icon">⚔️</div>
          <div className="connect-text">
            <h3>🛡️ Prepare for Battle</h3>
            <p>Connect your wallet to join the warriors and start earning epic dividends from every battle fought in the arena!</p>
            <div className="battle-features">
              <div className="feature">🎯 <span>2% of every bet becomes treasure</span></div>
              <div className="feature">⚡ <span>Daily battle cycles</span></div>
              <div className="feature">💎 <span>Claim rewards anytime</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const eligibilityStatus = getEligibilityStatus();

  return (
    <div className="dividend-panel">
      <div className="dividend-header">
        <h2>🏰 Warriors' Treasury</h2>
        <p>Epic rewards await the bravest warriors</p>
      </div>

      {error && (
        <div className="error-message">
          <span>⚠️ {error}</span>
        </div>
      )}

      <div className="dividend-grid">
        {/* Main Claimable Dividends - Hero Card */}
        <div className="dividend-card hero-card">
          <div className="card-header">
            <h3>💰 Battle Spoils</h3>
            <div className="card-icon treasure-chest">📦</div>
          </div>
          <div className="dividend-amount">
            {isLoading ? (
              <div className="loading-spinner">⚡</div>
            ) : (
              <>
                <span className="amount">
                  {formatTokenAmount(dividendData.claimableAmount)} 
                </span>
                <span className="currency">BLB</span>
              </>
            )}
          </div>
          <div className="claim-container">
            <button 
              className={`claim-button epic-button ${parseFloat(dividendData.claimableAmount) === 0 ? 'disabled' : ''}`}
              onClick={handleClaim}
              disabled={isClaiming || parseFloat(dividendData.claimableAmount) === 0 || isLoading}
            >
              {isClaiming ? (
                <><span className="spinner">⚡</span> Claiming Treasure...</>
              ) : parseFloat(dividendData.claimableAmount) > 0 ? (
                <><span className="icon">🚀</span> Claim Battle Spoils</>
              ) : (
                <><span className="icon">🛡️</span> No Spoils Available</>
              )}
            </button>
          </div>
        </div>

        {/* Current Battle Cycle */}
        <div className="dividend-card cycle-card">
          <div className="card-header">
            <h3>⏰ Battle Cycle #{cycleInfo.cycleId}</h3>
            <div className="card-icon">🔥</div>
          </div>
          <div className="cycle-stats">
            <div className="stat-item">
              <span className="stat-label">⏱️ Next Cycle In:</span>
              <span className="stat-value countdown">
                {cycleInfo.timeUntilNext > 0 ? formatTimeRemaining(cycleInfo.timeUntilNext) : 'Starting Soon'}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">⚔️ Active Warriors:</span>
              <span className="stat-value">{cycleInfo.eligiblePlayerCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">🏅 Your Status:</span>
              <span className={`stat-value ${eligibilityStatus.class}`}>
                {eligibilityStatus.text}
              </span>
            </div>
            {cycleInfo.lastEligibleCycle > 0 && (
              <div className="stat-item">
                <span className="stat-label">🎖️ Last Battle:</span>
                <span className="stat-value">Cycle #{cycleInfo.lastEligibleCycle}</span>
              </div>
            )}
          </div>
        </div>

        {/* Battle System Guide */}
        <div className="dividend-card guide-card">
          <div className="card-header">
            <h3>📜 Warrior's Guide</h3>
            <div className="card-icon">🗡️</div>
          </div>
          <div className="battle-guide">
            <div className="guide-step">
              <div className="step-icon">1️⃣</div>
              <div className="step-content">
                <strong>Enter the Arena</strong>
                <p>Place any bet in our games to become an active warrior</p>
              </div>
            </div>
            <div className="guide-step">
              <div className="step-icon">2️⃣</div>
              <div className="step-content">
                <strong>Earn from Battles</strong>
                <p>Receive 2% of every bet made by warriors after you</p>
              </div>
            </div>
            <div className="guide-step">
              <div className="step-icon">3️⃣</div>
              <div className="step-content">
                <strong>Daily Cycles</strong>
                <p>Battle cycles reset at midnight UTC every day</p>
              </div>
            </div>
            <div className="guide-step">
              <div className="step-icon">4️⃣</div>
              <div className="step-content">
                <strong>Claim Rewards</strong>
                <p>Your battle spoils accumulate - claim them anytime!</p>
              </div>
            </div>
          </div>
        </div>

        {/* Treasury Status */}
        <div className="dividend-card treasury-card">
          <div className="card-header">
            <h3>🏦 Treasury Status</h3>
            <div className="card-icon">💎</div>
          </div>
          <div className="treasury-stats">
            <div className="stat-item">
              <span className="stat-label">💰 Total Treasury:</span>
              <span className="stat-value">{formatTokenAmount(liquidityInfo.totalBalance)} BLB</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">⏳ Pending Claims:</span>
              <span className="stat-value">{formatTokenAmount(liquidityInfo.pendingDividends)} BLB</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">✨ Available Pool:</span>
              <span className="stat-value">{formatTokenAmount(liquidityInfo.availableLiquidity)} BLB</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dividend-info">
        <div className="info-box">
          <h4>🎯 Battle Mechanics</h4>
          <p>
            Every warrior who enters battle contributes 2% to the treasury. This treasure is immediately distributed 
            among all active warriors from the current cycle (excluding the current player). Battle cycles run for 24 hours 
            starting at midnight UTC. Join any battle to become eligible for dividends from all subsequent battles in that cycle!
          </p>
        </div>
      </div>
    </div>
  );
};

export default DividendPanel;